// Backend
import React from 'react';

// Frontend
import { Grid, Cell } from 'react-foundation';
import { PlusCircle } from 'react-bootstrap-icons';
import { Modal } from 'react-bootstrap';
import '../../stylesheets/components/Categories.css';

// Functionality
import { useNavigate } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

// Components
import {
  Header,
  ProgressBar,
  FlashMessage,
  ErrorRender,
} from '../../constants/SharedComponents';
import QuestionCategory from '../../components/QuestionCategory';
import QuestionCategoryContent from '../../components/QuestionCategoryContent';
import { useDemoContext } from '../../contexts/DemoContext';
import useCurrentUser from '../../hooks/useCurrentUser';

export function DemoCategoriesResults() {
  const navigate = useNavigate();
  const { demoData, updateDemoData, updateDemoPhase } = useDemoContext();
  const currentUser = useCurrentUser();

  // Demo state matching production structure
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [flashMessageObject] = React.useState({});
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [deselectedCategories, setDeselectedCategories] = React.useState([]);
  const [formChanged, setFormChanged] = React.useState(false);
  const [currentModal, setCurrentModal] = React.useState(null);
  const [inputs, setInputs] = React.useState({});
  const [allCategories, setAllCategories] = React.useState([]);
  const [categoryModal, setCategoryModal] = React.useState(false);
  const [categoryModalData, setCategoryModalData] = React.useState(null);
  const [infoModal, setInfoModal] = React.useState(false);
  const [infoModalData, setInfoModalData] = React.useState(null);

  const submissionUnlocked = true; // Always true for demo

  // Mock functions to match original props
  const categoryFormButtons = React.useRef({});

  // Get categories from fallback demo data (no API calls needed in demo)
  const getQuestionCategoriesData = async () => {
    setLoading(true);

    // Use fallback demo data directly instead of making API calls
    const fallbackCategories = [
      {
        id: 'ee048b5c-97b2-4f1e-bf90-0da6169729f8',
        name: 'Control',
        description: 'Control refers to the extent to which you can decide things about your work. Control can have various forms: it can be whether you have discretion on your working patterns, whether you can decide how to do things in your work.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: 'bb844672-2318-4a9f-b9ac-354f535c7dea',
        name: 'Management Support',
        description: 'Management support refers to the extent you feel that your line manager is providing you with appropriate support. Support can be technical advice, sufficient resources, information, emotional help.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: 'bae25492-5633-48ec-b04f-69f2bc9bc43b',
        name: 'Peer Support',
        description: 'Peer support is about the extent to which you perceive your colleagues to be supportive. It refers to the social support, encouragement and resources provided by work colleagues.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: '9608c2cc-6822-4a22-9ebf-4e5d7cab50f3',
        name: 'Role',
        description: 'Role refers to clarity about what is expected from you to do your job and how your work contributes to the wider organisation. In other words, what type of behaviours you should adopt to do your job well.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: '4af58c57-cbe4-499e-99ec-fb33c45c1b98',
        name: 'Change',
        description: 'Change relates about how workplace changes are managed. Change could include major organisations-level changes, such as restructures, merger, acquisition, new physical workspace, or new management.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: '8d0b0089-2cd2-4c4f-97ff-588710cba91d',
        name: 'Demands',
        description: 'Demands refers to aspects of your job that require effort. Work demands are very broad and varied across different jobs and they are really the core of what your job is about.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: '7185abba-d609-4071-b6b9-6e199c475e38',
        name: 'Relationships',
        description: 'Relationships refers to the quality of workplace relationships. Ideally, people should be working in a positive work atmosphere in which conflict is properly resolved and unacceptable behaviour is not tolerated.',
        categoryType: 0,
        selfGuided: false
      },
      {
        id: 'personalised-1',
        name: 'Personalised topic',
        description: 'Add your own personalized topic for discussion.',
        categoryType: 1,
        selfGuided: true
      },
      {
        id: 'personalised-2',
        name: 'Personalised topic',
        description: 'Add your own personalized topic for discussion.',
        categoryType: 1,
        selfGuided: true
      }
    ];

    setAllCategories(fallbackCategories);

    // Demands (selected), Peer Support (selected), and one Personalised topic (selected)
    const demandsCategory = fallbackCategories.find(cat => cat.name === 'Demands');
    const peerSupportCategory = fallbackCategories.find(cat => cat.name === 'Peer Support');
    const personalisedCategory = fallbackCategories.find(cat => cat.selfGuided === true);

    // Ensure we only have exactly 3 selected categories for guided route results
    const preselectedCategories = [demandsCategory, peerSupportCategory, personalisedCategory]
      .filter(Boolean)
      .slice(0, 3); // Limit to exactly 3 categories

    const remainingCategories = fallbackCategories.filter(cat =>
      !preselectedCategories.some(selected => selected.id === cat.id)
    );

    setSelectedCategories(preselectedCategories);
    setDeselectedCategories(remainingCategories);
    setError(null);
    setLoading(false);
  };

  // Initialize demo data
  React.useEffect(() => {
    if (currentUser?.token) {
      getQuestionCategoriesData();
    } else {
      // If no user token, use fallback data after a short delay
      setTimeout(() => {
        getQuestionCategoriesData();
      }, 500);
    }
  }, [currentUser]);

  // Handle checkboxes when modal opens (matching production)
  React.useEffect(() => {
    if (currentModal === 'category-private-notes') {
      handleCheckboxes();
    }
  }, [currentModal]);

  // Demo helper function to sync checkboxes with selected categories
  const handleCheckboxes = () => {
    for (const category of selectedCategories) {
      let categoryCheckbox = (Array.from(document.querySelectorAll('input[type="checkbox"]'))).find(i =>
        i.getAttribute("data-category-id") === category.id.toString());

      if (categoryCheckbox) {
        categoryCheckbox.checked = true;
      }
    }
  };

  const questionResultsContent = (questionCategoryId, field) => {
    if (inputs[field + '--' + questionCategoryId]) {
      return inputs[field + '--' + questionCategoryId];
    }
    return null;
  };
  // Add sorting function like production
  const sortedQuestionResultData = function (categoryData) {
    let responseData = categoryData.sort(function (a, b) {
      let weight = -1;

      if (a.priorityRank > b.priorityRank) {
        weight = 1;
      } else if (a.priorityRank === b.priorityRank) {
        weight = a.categoryName > b.categoryName ? 1 : -1;
      }

      return weight;
    });

    responseData = responseData.reverse();

    responseData = responseData.sort(function (a, b) {
      return a.categoryType - b.categoryType;
    });

    return responseData;
  };

  // Add handleSave function like production
  const handleSave = () => {
    // For demo, we don't actually save to API, just simulate the behavior
    setFormChanged(false);
  };

  const deselectCategory = (event) => {
    event.preventDefault();
    setFormChanged(true);
    setLoading(true);

    // Get category ID from the event target's data-category-id attribute
    const categoryId = event.target.getAttribute('data-category-id') ||
      event.currentTarget.getAttribute('data-category-id');

    // Find the category to remove
    const category = selectedCategories.find((obj) => obj.id.toString() === categoryId);

    if (category) {
      // Update form inputs to mark category as not a conversation topic
      setInputs(prev => ({
        ...prev,
        [`isConversationTopic--${category.id}`]: false
      }));

      // Remove the category from selectedCategories with sorting
      const updatedSelectedCategories = selectedCategories.filter(obj => obj.id !== category.id);
      setSelectedCategories(sortedQuestionResultData(updatedSelectedCategories));

      // Add the category to deselectedCategories
      setDeselectedCategories(prev => [...prev, category]);
    }

    setLoading(false);
    handleSave(); // Save immediately like production
  };

  const selectCategory = (event) => {
    event.preventDefault();
    setFormChanged(true);
    setLoading(true);

    // Find the category to add
    const categoryId = event.target.getAttribute('data-category-id');
    const category = deselectedCategories.find((obj) => obj.id === categoryId);

    if (category) {
      // Update form inputs to mark category as a conversation topic
      setInputs(prev => ({
        ...prev,
        [`isConversationTopic--${category.id}`]: true
      }));

      // Add the category to selectedCategories with sorting
      const updatedSelectedCategories = [...selectedCategories, category];
      setSelectedCategories(sortedQuestionResultData(updatedSelectedCategories));

      // Remove the category from deselectedCategories  
      setDeselectedCategories(prev =>
        prev.filter(obj => obj.id !== category.id)
      );
    }

    setLoading(false);
    handleSave(); // Save immediately like production
  };

  // Add effect to handle checkboxes when modal opens
  React.useEffect(() => {
    if (currentModal === 'category-private-notes') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        handleCheckboxes();
      }, 100);
    }
  }, [currentModal]);

  const selectCategories = (e) => {
    e.preventDefault(); // Prevent form submission
    setFormChanged(true);
    setLoading(true);

    // Clear current selected categories first (like production)
    let selectedCategoriesCopy = selectedCategories.slice();

    for (const category of selectedCategoriesCopy) {
      if (category) {
        setInputs(prev => ({
          ...prev,
          [`isConversationTopic--${category.id}`]: false
        }));
      }
    }

    // Get all checked categories from the modal
    let allCheckedCategories = Array.from(document.querySelectorAll('input[type="checkbox"]:checked'))
      .map(checkbox => allCategories.find((obj) => {
        return obj.id === checkbox.getAttribute('data-category-id');
      }))
      .filter(Boolean); // Remove undefined entries

    // Limit to 3 categories
    let categories = allCheckedCategories.slice(0, 3);

    // Update form inputs for selected categories
    for (const category of categories) {
      if (category) {
        setInputs(prev => ({
          ...prev,
          [`isConversationTopic--${category.id}`]: true
        }));
      }
    }

    // Update selected and deselected categories with sorting
    setSelectedCategories(sortedQuestionResultData(categories));
    setDeselectedCategories(allCategories.filter(cat =>
      !categories.some(selected => selected.id === cat.id)
    ));

    setLoading(false);
    setCurrentModal(null); // Close modal
    handleSave(); // Save immediately like production
  };

  const handleGoBack = (e) => {
    e.preventDefault(); // Prevent form submission
    navigate('/demo/questionnaire');
  };

  const handleSaveChanges = (e) => {
    e.preventDefault(); // Prevent form submission

    // Update demo phase to topics selected
    updateDemoPhase('topics-selected');

    // Update demo context to mark categories as selected
    updateDemoData({
      ...demoData,
      selectedCategories: selectedCategories,
      currentStep: 'categories-complete'
    });

    // Navigate to the confirmation/review page in demo
    navigate('/demo/confirmation');
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
    setFormChanged(true);
  };
  function handleCloseCategoryModal() {
    setCategoryModal(false);
    setCategoryModalData(null);
  }

  function handleOpenInfoModal(e, category) {
    e.preventDefault();
    setInfoModal(true);
    setInfoModalData(category);
  }
  function handleCloseInfoModal() {
    setInfoModal(false);
    setInfoModalData(null);
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Global styles for demo category colors */}
      <style>
        {`
          .category-form-field__title--change {
            color: var(--color--green-turquoise--800) !important;
          }
          .category-form-field__title--relationships {
            color: var(--color--green-alternative--500) !important;
          }
          .category-form-field__title--demands {
            color: var(--color--green--500) !important;
          }
          .category-form-field__title--role {
            color: var(--color--green-turquoise-alternative--500) !important;
          }
          .category-form-field__title--peer-support {
            color: var(--color--green-turquoise-alternative-dark--500) !important;
          }
          .category-form-field__title--management-support {
            color: var(--color--blue--500) !important;
          }
          .category-form-field__title--control {
            color: var(--color--blue--300) !important;
          }
          .category-form-field__title--personalised,
          .category-form-field__title--12e9af3e-fb1d-4d4a-952f-9f91b5f5ad20,
          .category-form-field__title--1c3ab099-91c9-4e78-9fb4-850fa64599e0,
          .category-form-field__title--757cb486-0cee-4e3a-b885-ee6df58cffbd,
          .category-form-field__title--a3caeb12-192b-4570-9e21-1ce30f4c99c9,
          .category-form-field__title--personalised-1,
          .category-form-field__title--personalised-2 {
            color: var(--color--brand-secondary) !important;
          }
        `}
      </style>
      <div style={{ flex: '1 0 auto' }}>
        <ProgressBar loading={loading} />
        <Header />
        <FlashMessage object={flashMessageObject} columnWrap={true} />
        <ErrorRender object={error} />

        <div className="grid-container">
          <Grid className="grid-padding-x">
            <Cell small={12}>
              <form onSubmit={(e) => e.preventDefault()}>
                {/* Survey Complete Notice - exactly matching original */}
                <div className="page-notice">
                  <h1 className="page-notice__title">Survey complete</h1>
                  <div className="page-notice__content">
                    <p>
                      Based on your scores, we have preselected three suggested topics for your
                      conversation. These are the three areas in which your score suggested most
                      prevalent risks to your wellbeing. You can read more about how the results
                      are calculated in our{' '}
                      <a href="/resources" target="_blank" rel="noopener noreferrer">
                        resources area
                      </a>.
                    </p>
                  </div>
                </div>

                {/* Action Buttons - exactly matching original */}
                <div style={{ position: 'relative' }}>
                  <button
                    className="page-content__button--medium page-content__button--back mb-3"
                    id="page-navigation-button--go-back"
                    onClick={handleGoBack}
                  >
                    Go back
                  </button>

                  <button
                    className="page-content__button--medium mb-3"
                    id="page-navigation-button--save-changes"
                    onClick={handleSaveChanges}
                    disabled={!formChanged}
                    style={{ position: 'absolute', right: '0', bottom: '0' }}
                  >
                    Save Changes
                  </button>
                </div>

                {/* What you need to do section - exactly matching original */}
                <div className="page-content">
                  <h3 className="page-content__subtitle page-content__subtitle--section">
                    What you need to do
                  </h3>
                </div>
              </form>

              {/* Categories Form - exactly matching original structure */}
              <form
                className="basic-form"
                id="selected-categories"
              >
                <div className="question-categories-choice">
                  {/* Review topics section - exactly matching original */}
                  <h2 className="question-categories-choice__title">
                    1) Review the conversation topics
                  </h2>

                  <div className="question-categories-choice__styles">
                    <p className="queestion-categories-choice__styles--microcopy">
                      When considering the topics to take to your manager meeting, please
                      take time to explore them and reflect upon whether and how they
                      cause you stress.
                      We have a useful guide on how to complete your team member prep in
                      our resources area.{' '}
                      <a
                        href={process.env.PUBLIC_URL + '/Team Member Preparation Guidance.pdf'}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View team member prep guide.
                      </a>
                    </p>
                  </div>

                  {/* Categories Grid - exactly matching original */}
                  <Grid className="grid-padding-x">
                    {selectedCategories?.map((questionCategory) => (
                      <Cell
                        small={12}
                        large={4}
                        key={questionCategory.id}
                        style={{ display: 'flex', flexDirection: 'column' }}
                      >
                        {/* Use actual QuestionCategory component like original */}
                        <QuestionCategory
                          categoryFormButtons={categoryFormButtons}
                          closeModal={handleCloseInfoModal}
                          currentModal=""
                          deselectCategory={deselectCategory}
                          handleChange={handleChange}
                          openModal={(e) => handleOpenInfoModal(e, questionCategory)}
                          questionCategory={questionCategory}
                          questionResultsContent={questionResultsContent}
                          selectCategory={null}
                          selectedCategories={selectedCategories || []}
                          submissionUnlocked={submissionUnlocked}
                          showSummary={true}
                          showNotes={false}
                        />
                        {submissionUnlocked && (
                          <div className="category-notes-outer">
                            <textarea
                              className="category-notes"
                              placeholder="Topic Notes..."
                              value={inputs[`notes--${questionCategory.id}`] || ''}
                              data-complete="true"
                              onChange={handleChange}
                              name={`notes--${questionCategory.id}`}
                            />
                          </div>
                        )}
                      </Cell>
                    ))}

                    {/* Add Topic Placeholders - exactly matching original */}
                    {submissionUnlocked &&
                      selectedCategories.length < 3 &&
                      Array.from({ length: 3 - selectedCategories.length }).map((_, i) => (
                        <Cell small={12} large={4} key={'pending-category-' + i}>
                          <div className="category-form-field-wrap">
                            <div className="category-form-field category-form-field--empty">
                              <HashLink
                                to="#deselected-categories"
                                className="category-form-field__button category-form-field__button--secondary"
                                id="page-function-button--add"
                                onClick={() => setCurrentModal('category-private-notes')}
                                data-modal-id="category-private-notes"
                              >
                                Add topic
                                <PlusCircle
                                  size={21}
                                  className="category-form-field__button-icon category-form-field__button-icon--empty util--hide-icon"
                                />
                              </HashLink>
                            </div>
                          </div>
                        </Cell>))}
                  </Grid>
                </div>

                {/* Section 2: General Notes - exactly matching original */}
                <div className="question-categories-choice">
                  <h2 className="question-categories-choice__title">2) General Notes</h2>
                  <div className="question-categories-choice__styles question-categories-choice__styles--microcopy">
                    <p className="queestion-categories-choice__styles--microcopy">
                      Take time to understand the topics and review the examples then make any notes that you feel will help you to ensure that you include everything you would like to communicate in the meaning.
                    </p>
                  </div>
                  <div className="category-notes-outer">
                    <textarea
                      id="general_notes"
                      name="general_notes"
                      placeholder="General Notes..."
                      className="category-notes"
                      data-complete="true"
                      value={inputs.general_notes || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Section 3: Submit your prep - exactly matching original */}
                <div className="question-categories-choice">
                  <h2 className="question-categories-choice__title">
                    3) Submit your prep
                  </h2>

                  <Grid className="grid-padding-x">
                    <Cell small={12} large={9}>
                      <div className="question-categories-choice__styles question-categories-choice__styles--microcopy">
                        <p className="queestion-categories-choice__styles--microcopy">When you are happy with the topics you have chosen</p>
                      </div>
                    </Cell>
                    <Cell small={12} large={3} className="text-right">
                      {formChanged && selectedCategories.length >= 1 && (
                        <div className="basic-form__actions basic-form__actions--inline">
                          <input
                            value="Next Step"
                            data-complete="true"
                            type="submit"
                            className="basic-form__button basic-form__button--inline"
                            onClick={handleSaveChanges}
                            id="page-form-button--complete"
                          />
                        </div>
                      )}

                      {!formChanged && selectedCategories.length >= 1 && (
                        <Grid className="grid-padding-x">
                          <Cell small={12} className="text-right">
                            <button
                              className="page-content__button--medium"
                              id="page-form-button--complete"
                              onClick={handleSaveChanges}
                            >
                              Next Step
                            </button>
                          </Cell>
                        </Grid>
                      )}
                    </Cell>
                  </Grid>
                </div>

                {/* Modal for adding topics - exactly matching original */}
                {currentModal === 'category-private-notes' && (
                  <>
                    <style>
                      {`
                        .demo-modal .category-form-field__title {
                          color: var(--color--green-turquoise--800) !important;
                        }
                        .demo-modal .basic-form__button {
                          color: white !important;
                        }
                        .demo-modal .modal-title {
                          color: inherit !important;
                        }
                        .demo-modal .btn-close-categories {
                          color: inherit !important;
                        }
                        .demo-modal .category-form-field__title--change {
                          color: var(--color--green-turquoise--800) !important;
                        }
                        .demo-modal .category-form-field__title--relationships {
                          color: var(--color--green-alternative--500) !important;
                        }
                        .demo-modal .category-form-field__title--demands {
                          color: var(--color--green--500) !important;
                        }
                        .demo-modal .category-form-field__title--role {
                          color: var(--color--green-turquoise-alternative--500) !important;
                        }
                        .demo-modal .category-form-field__title--peer-support {
                          color: var(--color--green-turquoise-alternative-dark--500) !important;
                        }
                        .demo-modal .category-form-field__title--management-support {
                          color: var(--color--blue--500) !important;
                        }
                        .demo-modal .category-form-field__title--control {
                          color: var(--color--blue--300) !important;
                        }
                        .demo-modal .category-form-field__title--personalised,
                        .demo-modal .category-form-field__title--12e9af3e-fb1d-4d4a-952f-9f91b5f5ad20,
                        .demo-modal .category-form-field__title--1c3ab099-91c9-4e78-9fb4-850fa64599e0,
                        .demo-modal .category-form-field__title--757cb486-0cee-4e3a-b885-ee6df58cffbd,
                        .demo-modal .category-form-field__title--a3caeb12-192b-4570-9e21-1ce30f4c99c9 {
                          color: var(--color--brand-secondary) !important;
                        }
                      `}
                    </style>
                    <div
                      className="modal fade show demo-modal"
                      tabIndex="-1"
                      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                    >
                      <Modal show={currentModal === 'category-private-notes'} dialogClassName='new-modal-dialog' className='topic-modal'>
                        <Modal.Header className='justify-content-center'>
                          <div className="modal-header border-0" style={{ position: 'relative' }}>
                            <h5 className="modal-title w-100 text-center">
                              Select Your Conversation Topics (Max 3)
                            </h5>
                          </div>
                          <button
                            type="button"
                            className="btn-close-categories"
                            aria-label="Close"
                            onClick={() => setCurrentModal(null)}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 2 }}
                          >
                            <span aria-hidden="true">&times;</span>
                          </button>
                        </Modal.Header>

                        <Modal.Body>
                          <div className="category-form-modal">
                            {submissionUnlocked && (
                              <div className="grid-container">
                                <Grid className="grid-padding-x">
                                  <Cell small={12}>
                                    {(selectedCategories || deselectedCategories) && (
                                      <Grid className="grid-padding-x">
                                        {[...selectedCategories, ...deselectedCategories].map((questionCategory) => (
                                          <Cell
                                            small={12}
                                            large={4}
                                            key={'modal-question-' + questionCategory.id}
                                          >
                                            <QuestionCategory
                                              categoryFormButtons={categoryFormButtons}
                                              closeModal={handleCloseInfoModal}
                                              currentModal={infoModal && infoModalData?.id === questionCategory.id ? `question-category-content--${questionCategory.id}` : ''}
                                              deselectCategory={null}
                                              handleChange={handleChange}
                                              openModal={(e) => handleOpenInfoModal(e, questionCategory)}
                                              questionCategory={questionCategory}
                                              questionResultsContent={questionResultsContent}
                                              selectCategory={selectCategory}
                                              selectedCategories={selectedCategories || []}
                                              submissionUnlocked={submissionUnlocked}
                                              showSummary={true}
                                              showNotes={true}
                                            />
                                          </Cell>
                                        ))}
                                      </Grid>
                                    )}
                                  </Cell>
                                </Grid>
                              </div>
                            )}
                          </div>
                        </Modal.Body>
                        <Modal.Footer className='justify-content-center'>
                          <div className="modal-footer border-0 justify-content-center">
                            <button
                              className="page-content__button--medium mb-3"
                              id="page-navigation-button--save-changes"
                              onClick={selectCategories}
                              style={{ color: 'white' }}
                            >
                              Save Chosen Topics
                            </button>
                          </div>
                        </Modal.Footer>
                      </Modal>
                    </div>
                  </>
                )}
              </form>
            </Cell>
          </Grid>
        </div>
      </div>

      {/* Individual category content modals */}
      {allCategories.map((category) => (
        infoModal && infoModalData?.id === category.id && (
          <div
            key={`modal-${category.id}`}
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title question-category-modal-title w-100 text-center">
                    {category.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close-categories"
                    onClick={handleCloseInfoModal}
                    aria-label="Close"
                  >&times;</button>
                </div>
                <div className="modal-body">
                  <QuestionCategoryContent categoryId={category.id} />
                </div>
              </div>
            </div>
          </div>
        )
      ))}

      {/* Category detail modal - new addition */}
      {categoryModal && categoryModalData && (
        <div className="modal fade show d-block ReactModal__Overlay" tabIndex="-1" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                    {categoryModalData.name}
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={handleCloseCategoryModal}
                    style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
                  >
                    &times;
                  </button>
                </div>
                <div style={{ padding: '18px 0', textAlign: 'left' }}>
                  <h4 style={{ fontWeight: 600, marginBottom: 8 }}>What is {categoryModalData.name} about?</h4>
                  <p style={{ marginBottom: 16 }}>{categoryModalData.description}</p>
                  {/* Add more details here if needed, e.g. examples, notes, etc. */}
                </div>
                <div className="mb-3" style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                  <button
                    onClick={handleCloseCategoryModal}
                    className="page-content__button--medium page-content__button--secondary"
                    type="button"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
